<template>
    <div class="page-container" :class="theme">
        <canvas class="particles"></canvas>

        <button class="theme-toggle" @click="toggleTheme" :aria-label="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'">
            <svg v-if="theme === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
        </button>

        <button class="back-button" @click="$router.push('/')">← Back</button>
        <div class="progress-bar" :style="{ width: scrollProgress + '%' }"></div>

        <div class="main-container">
            <header class="section-header">
                <h1 class="section-title">Projects</h1>
                <p class="section-subtitle">{{ projects.length }} things I've built</p>
                <div class="section-divider"></div>
            </header>

            <div class="project-grid">
                <div class="project-card"
                     v-for="(item, index) in projects"
                     :key="index"
                     :style="{ animationDelay: index * 0.08 + 's' }"
                     :class="{ active: activeIndex === index }">

                    <!-- Collapsed header -->
                    <div class="card-header" @click="toggleProject(index)">
                        <img :src="item.Image" :alt="item.Title" class="card-thumbnail">
                        <div class="card-meta">
                            <span class="project-number">{{ String(index + 1).padStart(2, '0') }}</span>
                            <span class="project-title">{{ item.Title.replace(/^Project \d+:\s*/, '') }}</span>
                        </div>
                        <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <polyline points="6 9 12 15 18 9"/>
                        </svg>
                    </div>

                    <!-- Expanded content -->
                    <transition name="slide-fade" @enter="startTransition" @leave="endTransition">
                        <div v-show="activeIndex === index"
                             class="dropdown-content"
                             :class="{ visible: activeIndex === index }"
                             @click.stop>

                            <!-- Description -->
                            <ul class="description-list">
                                <li v-if="item.About" class="description-item">{{ item.About }}</li>
                                <li v-if="item.About1" class="description-item">{{ item.About1 }}</li>
                                <li v-if="item.About2" class="description-item">{{ item.About2 }}</li>
                            </ul>

                            <!-- Image gallery -->
                            <div class="gallery">
                                <img :src="item.Image" alt="Screenshot" class="gallery-image" loading="lazy">
                                <img v-if="item.Image1" :src="item.Image1" alt="Screenshot" class="gallery-image" loading="lazy">
                                <img v-if="item.Image2" :src="item.Image2" alt="Screenshot" class="gallery-image" loading="lazy">
                                <img v-if="item.Image3" :src="item.Image3" alt="Screenshot" class="gallery-image" loading="lazy">
                                <img v-if="item.Image4" :src="item.Image4" alt="Screenshot" class="gallery-image" loading="lazy">
                                <img v-if="item.Image5" :src="item.Image5" alt="Screenshot" class="gallery-image" loading="lazy">
                                <img v-if="item.Image6" :src="item.Image6" alt="Screenshot" class="gallery-image" loading="lazy">
                            </div>

                            <!-- Links -->
                            <div class="link-row">
                                <a v-if="item.Link" :href="item.Link" target="_blank" rel="noopener noreferrer" class="link-button" @click.stop>
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>
                                    GitHub ↗
                                </a>
                                <a v-if="item.DevpostLink" :href="item.DevpostLink" target="_blank" rel="noopener noreferrer" class="link-button link-devpost" @click.stop>
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M6.002 1.61L0 12.004 6.002 22.39h11.996L24 12.004 17.998 1.61zm1.593 4.084h3.947c3.605 0 6.276 1.695 6.276 6.31 0 4.436-3.21 6.302-6.456 6.302H7.595zm2.517 2.449v7.714h1.241c2.646 0 3.862-1.55 3.862-3.861.009-2.569-1.096-3.853-3.767-3.853z"/></svg>
                                    Devpost ↗
                                </a>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import home_page from "../assets/home_page.png";
import creation_workout from "../assets/creation_workout.png";
import progress from "../assets/progress.png";
import posts from "../assets/posts.png";
import food_diary from "../assets/food_diary.png";
import workout_diary from "../assets/workout_diary.png";
import settings from "../assets/settings.png";
import qubits from "../assets/qubits.png";
import qubitsR from "../assets/qubit-results.png"
import datafest from "../assets/datafest.png"
import datafest1 from "../assets/datafest1.png"
import triage from "../assets/triage.png"
import triage1 from "../assets/triage1.png"
import wug from "../assets/wug.png"
import rfc from "../assets/RFC.png"
import rf from "../assets/rf.png"
import recommender from "../assets/MovieRecommendation.png"
import ext2 from "../assets/EXT2.png"
import wug1 from "../assets/wug1.png"
import mutex from "../assets/mutex.png"
import VQE from "../assets/VQE.png"
import vanguard from "../assets/vanguard.png"
import catholic from "../assets/catholic.png"
import catholic_1 from "../assets/catholic_1.png"
import filesystem from "../assets/filesystem.png";
import cpp from "../assets/c++.png";

export default {
    data() {
        return {
            theme: 'dark',
            projects: [
                {
                    id: 1,
                    Title: "Project 1: Fitica - Cross-Platform Mobile Fitness App",
                    Image: home_page,
                    Image1: creation_workout,
                    Image2: progress,
                    Image3: posts,
                    Image4: food_diary,
                    Image5: workout_diary,
                    Image6: settings,
                    About: "Developed a cross-platform mobile fitness app using React Native + TypeScript (iOS), aimed at providing users with personalized workouts and nutrition plans.",
                    About1: "Built and maintained key features including user authentication (Google OAuth), profile management, workout logging & tracking, food diary with nutrition search, calorie & weight tracking, and a dynamic theme system (light, dark, glassmorphic, claymorphic, etc.).",
                    About2: "Ensured responsive design across different devices (iphones 11-13), and implemented maintainable, modular code structure with React Context, typed definitions, utilities for styling and responsiveness, and clear separation of concerns (components, services, backend, contexts, data, etc.). Collaborated with 4 fellow developers to iterate quickly on features, manage code through version control (Git), and coordinate across frontend & backend to deliver an end-to-end functional fitness application, looking to officially publish on the App store by October 2026",
                    Link: ""
                },
                {
                    id: 2,
                    Title: "Project 2: FLIQ Virtual Hackathon - Quantum Machine Learning for Drug-Induced Autoimmunity",
                    Image: qubits,
                    Image1: qubitsR,
                    About: "Developed hybrid quantum-classical machine learning framework combining traditional ML techniques with quantum computing principles to enhance drug-induced autoimmunity prediction accuracy and model interpretability.",
                    About1: "Implemented quantum feature mapping and variational quantum classifier achieving 85.7% classification accuracy on training set with 7.2% improvement over classical baselines (Random Forest, SVM) on Drug Induced Autoimmunity Prediction dataset.",
                    About2: "Conducted comprehensive comparative analysis of quantum vs classical approaches, documenting performance metrics, computational complexity, and scalability considerations for pharmaceutical applications.",
                    Link: "https://github.com/Clyde0513/FLIQ-Virtual-Hackathon"
                },
                {
                    id: 3,
                    Title: "Project 3: UCLA DataFest 2025 - Time-Series Healthcare Real Estate Market Analysis",
                    Image: datafest,
                    Image1: datafest1,
                    About: "Led 5-member interdisciplinary team in comprehensive analysis of commercial real estate leasing patterns, developing predictive models for high-opportunity healthcare leasing markets using 10+ years of historical data.",
                    About1: "Engineered time-series features from quarterly leasing data, incorporated COVID-19 economic impact indicators, and implemented ensemble learning approaches with calibrated probability outputs.",
                    About2: "Achieved significant model performance improvements: ROC-AUC scores increased from 0.46 (2021 baseline) to 0.71 (2023 optimized model), securing finalist position among 400+ participants at UCLA DataFest 2025. Presented findings to industry judges including real estate professionals and data scientists, receiving recognition for innovative feature engineering and business impact analysis.",
                    Link: "https://github.com/Clyde0513/datafest_2025"
                },
                {
                    id: 4,
                    Title: "Project 4: Tiwala Kapwa - Church Community Platform",
                    Image: catholic,
                    Image1: catholic_1,
                    About: "Built for Kapwa Codefest 2025 Hackathon - A comprehensive church community management platform for Filipino Apostolates featuring event management, blog/content management, photo & video galleries, and community engagement tools. Developed using Next.js 15.5+ with TypeScript, Tailwind CSS, PostgreSQL via Prisma ORM, and Sanity.io CMS.",
                    About1: "Implemented full-stack features including CRUD operations for events with FullCalendar integration, admin dashboard for church staff, user engagement system with comments and likes, and responsive mobile-first design. Integrated Sanity Studio for rich content creation and database-driven content management.",
                    About2: "Deployed on Vercel with PostgreSQL database, featuring admin authentication, interactive calendar views, mass schedule display, and comprehensive API routes for events, posts, comments, and media management. Created to empower Filipino Catholic communities through modern technology.",
                    Link: "https://github.com/Clyde0513/kapwa-codefest-2025-Tiwala-Faith-",
                    DevpostLink: "https://devpost.com/software/tiwala?ref_content=my-projects-tab&ref_feature=my_projects"
                },
                {
                    id: 5,
                    Title: "Project 5: Quantum Portfolio Optimization - Hackathon Project",
                    Image: vanguard,
                    Image1: VQE,
                    About: "Developed a quantum approach to portfolio optimization using Variational Quantum Eigensolver (VQE) with QAOA ansatz for The Wiser's Quantum for Portfolio Optimization hackathon (We placed in the top 9 Finalist). Implemented hybrid quantum-classical framework to solve Quadratic Unconstrained Binary Optimization (QUBO) problems for real-world bond portfolio optimization.",
                    About1: "Achieved quantum advantage with Vanguard bond portfolio data ($2.6B+ market value), demonstrating superior constraint satisfaction compared to classical methods. Used 20-24 qubits with Lightning.GPU backend, achieving 6.1% average returns with optimized risk-return profiles and reduced constraint violations.",
                    About2: "Implemented advanced features including adaptive penalty systems, multi-restart QAOA optimization (3-15 layers deep), and comprehensive classical benchmarking using simulated annealing. Project showcased scalability potential for real-world portfolios with 50-500+ assets where quantum computing provides exponential advantage over classical optimization.",
                    Link: "https://github.com/Clyde0513/quantum_for_portfolio_optimization"
                },
                {
                    id: 6,
                    Title: "Project 6: TriageFlow Care - AI-Powered Healthcare Triage System (WIP)",
                    Image: triage,
                    Image1: triage1,
                    About: "Work-in-progress project developing an intelligent healthcare triage system to optimize patient flow and resource allocation in healthcare facilities.",
                    // About1: "Implementing machine learning algorithms to analyze patient symptoms, medical history, and real-time data to provide accurate triage recommendations and priority scoring.",
                    About2: "Built a comprehensive dashboard for healthcare professionals with real-time analytics, patient queuing system, and predictive modeling for capacity planning and emergency response optimization.",
                    Link: "https://github.com/aliiyuu/triageflow-care"
                },
                {
                    id: 7,
                    Title: "Project 7: Wug Bot 3.0 using Python and NLTK",
                    Image: wug1,
                    Image1: wug,
                    About: "Co-developed Wug Bot 3.0, a linguistics-focused Discord bot capable of performing various linguistic analyses. This bot supports morphological, phonetic (IPA), syntactic, and semantic processing of sentences. It leverages the Natural Language Toolkit (NLTK) for linguistic data processing, WordNet for enhanced semantic analysis, and the ArgosTranslate library to facilitate translations across 13 languages, with plans to support additional languages in the near future.",
                    About1: "",
                    About2: "",
                    Link: "https://github.com/Clyde0513/Wug-Bot-3.0"
                },
                {
                    id: 8,
                    Title: "Project 8: Using Different Machine Learning Models to Predict Whether a Hotel Booking Will be Canceled or Not",
                    Image: rfc,
                    Image1: rf,
                    About: "After experimenting with different ML models, a model called RandomForest gets this job done accurately. It fits a decision tree classifiers on various sub-samples of the Hotel Booking dataset, and then averaging to improve its predictive accuracy and control over-fitting. Since the dataset is huge, roughly 69k samples, this model is accurate because it constructs multiple decision trees at the same training time. Lastly, it handles non-linearly data well. As a result, the model got an accuracy of 87%.",
                    About1: "",
                    About2: "",
                    Link: "https://github.com/Clyde0513/DataScience-Project-1/tree/main"
                },
                {
                    id: 9,
                    Title: "Project 9: Recommender System using C++",
                    Image: recommender,
                    Image1: cpp,
                    About: "It can recommend up to 100k movies based on its user and movie databases. I used unordered hash map, vectors and an algorithm called Tree Multimap to sort the movie and users from its respective databases and gather recommendations from them based on a certain criteria. So the complexities can range from different kinds of directors, genres, actors, e.g O(Log(D)) to O(log(A))",
                    About1: "",
                    About2: "",
                    Link: "https://github.com/Clyde0513/CS32-TreeMultimap-Database"
                },
                {
                    id: 10,
                    Title: "Project 10: EXT2 Filesystem in C",
                    Image: ext2,
                    Image1: filesystem,
                    About: "Implemented a EXT2 Filesystem in C so that the kernal could mount it. This is a 1 MiB EXT2 FS with 1000 sized blocks and has a space for 128 inodes. It has 4 inodes: the root directory, lost+found directory, 1 regular file called hello+world, and 1 symbolic link that points to hello+world called hello. To test the FileSystem, make a directory called mnt. Then do sudo mount -o loop cs111-base.img mnt to mount your EXT2 filesystem.",
                    About1: "",
                    About2: "",
                    Link: "https://github.com/Clyde0513/EXT2-FileSystem-C-Kernel"
                },
                {
                    id: 11,
                    Title: "Project 11: Hash-Hash in C",
                    Image: mutex,
                    About: "I experimented 3 different hash table implementations in which the base is the serial hash implementation while the others are not serial. In theory, V1 should be slower than base while V2 is way faster than V1 and base combined.",
                    About1: "",
                    About2: "",
                    Link: "https://github.com/Clyde0513/Hash-Hash-in-C"
                }
            ],
            activeIndex: null,
            scrollProgress: 0
        }
    },
    mounted() {
        this.initParticles();
        window.addEventListener('scroll', this.updateScrollProgress);
        this.theme = localStorage.getItem('theme') || 'dark';
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.updateScrollProgress);
    },
    methods: {
        toggleProject(index) {
            this.activeIndex = this.activeIndex === index ? null : index;
        },
        startTransition(el) {
            const height = el.scrollHeight;
            el.style.height = '0';
            el.offsetHeight; // force reflow
            el.style.height = height + 'px';
        },
        endTransition(el) {
            el.style.height = el.scrollHeight + 'px';
            el.offsetHeight; // force reflow
            el.style.height = '0';
        },
        initParticles() {
            const canvas = document.querySelector('.particles');
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            const particles = [];
            
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            
            window.addEventListener('resize', resizeCanvas);
            resizeCanvas();

            class Particle {
                constructor() {
                    this.x = Math.random() * canvas.width;
                    this.y = Math.random() * canvas.height;
                    this.size = Math.random() * 3 + 1;
                    this.speedX = Math.random() * 3 - 1.5;
                    this.speedY = Math.random() * 3 - 1.5;
                    this.opacity = Math.random() * 0.5 + 0.3;
                }

                update() {
                    this.x += this.speedX;
                    this.y += this.speedY;

                    if (this.x > canvas.width) this.x = 0;
                    if (this.x < 0) this.x = canvas.width;
                    if (this.y > canvas.height) this.y = 0;
                    if (this.y < 0) this.y = canvas.height;
                }

                draw() {
                    ctx.fillStyle = `rgba(52, 152, 219, ${this.opacity})`;
                    ctx.beginPath();
                    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            const createParticles = () => {
                for (let i = 0; i < 50; i++) {
                    particles.push(new Particle());
                }
            };

            const animate = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                particles.forEach(particle => {
                    particle.update();
                    particle.draw();
                });
                requestAnimationFrame(animate);
            };

            createParticles();
            animate();
        },
        updateScrollProgress() {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            this.scrollProgress = (scrollTop / docHeight) * 100;
        },
        toggleTheme() {
            this.theme = this.theme === 'dark' ? 'light' : 'dark';
            localStorage.setItem('theme', this.theme);
        },
    }
}
</script>

<style scoped>
/* =====================
   CSS Variables / Theming
   ===================== */
.page-container {
    min-height: 100vh;
    position: relative;
    transition: background-color 0.3s ease, color 0.3s ease;
}

.page-container.dark {
    --bg: #0d2535;
    --text-primary: #cce0ec;
    --text-secondary: #7aaabf;
    --card-bg: rgba(255, 255, 255, 0.03);
    --card-border: rgba(255, 255, 255, 0.07);
    --card-hover-border: rgba(74, 159, 200, 0.35);
    --accent: #5eb8de;
    --accent-subtle: rgba(74, 159, 200, 0.1);
    --accent-border: rgba(74, 159, 200, 0.28);
    --tag-text: #7dd3f0;
    --ui-border: rgba(255, 255, 255, 0.1);
    --ui-bg: rgba(255, 255, 255, 0.06);
    --ui-bg-hover: rgba(255, 255, 255, 0.11);
    --number-color: rgba(94, 184, 222, 0.25);
    background-color: var(--bg);
    color: var(--text-primary);
}

.page-container.light {
    --bg: #f0f5f9;
    --text-primary: #182635;
    --text-secondary: #4a6678;
    --card-bg: rgba(255, 255, 255, 0.9);
    --card-border: rgba(0, 0, 0, 0.08);
    --card-hover-border: rgba(26, 111, 160, 0.3);
    --accent: #1a6fa0;
    --accent-subtle: rgba(26, 111, 160, 0.08);
    --accent-border: rgba(26, 111, 160, 0.25);
    --tag-text: #1a6fa0;
    --ui-border: rgba(0, 0, 0, 0.1);
    --ui-bg: rgba(0, 0, 0, 0.04);
    --ui-bg-hover: rgba(0, 0, 0, 0.08);
    --number-color: rgba(26, 111, 160, 0.2);
    background-color: var(--bg);
    color: var(--text-primary);
}

/* =====================
   Fixed UI Elements
   ===================== */
.particles {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    z-index: 0;
}

.progress-bar {
    position: fixed;
    top: 0; left: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent), transparent);
    z-index: 101;
    transition: width 0.1s linear;
}

.back-button,
.theme-toggle {
    position: fixed;
    top: 20px;
    background: var(--ui-bg);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid var(--ui-border);
    border-radius: 7px;
    color: var(--text-primary);
    cursor: pointer;
    z-index: 100;
    transition: background 0.2s ease, border-color 0.2s ease;
    font-family: inherit;
}

.back-button {
    left: 20px;
    padding: 0.45rem 1rem;
    font-size: 0.875rem;
}

.theme-toggle {
    right: 20px;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.back-button:hover,
.theme-toggle:hover {
    background: var(--ui-bg-hover);
    border-color: var(--accent-border);
}

.theme-toggle svg {
    width: 17px;
    height: 17px;
    stroke: var(--text-primary);
}

/* =====================
   Layout
   ===================== */
.main-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 5.5rem 2rem 4rem;
    position: relative;
    z-index: 1;
}

/* =====================
   Section Header
   ===================== */
.section-header {
    margin-bottom: 2.5rem;
}

.section-title {
    font-size: 1.9rem;
    font-weight: 700;
    letter-spacing: -0.025em;
    margin: 0 0 0.25rem;
    color: var(--text-primary);
}

.section-subtitle {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin: 0 0 0.6rem;
    font-weight: 400;
}

.section-divider {
    height: 2px;
    width: 48px;
    background: linear-gradient(90deg, var(--accent), transparent);
    border-radius: 2px;
}

/* =====================
   Project Grid
   ===================== */
.project-grid {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.project-card {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-left: 3px solid transparent;
    border-radius: 10px;
    overflow: hidden;
    opacity: 0;
    transform: translateY(14px);
    animation: fadeInUp 0.4s ease forwards;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.project-card:hover,
.project-card.active {
    border-left-color: var(--accent);
    border-color: var(--card-hover-border);
    box-shadow: 0 4px 24px rgba(74, 159, 200, 0.08);
}

/* =====================
   Card Header
   ===================== */
.card-header {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 0.85rem 1.3rem;
    cursor: pointer;
    user-select: none;
}

.card-thumbnail {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
    border: 1px solid var(--card-border);
}

.card-meta {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
}

.project-number {
    font-size: 1.3rem;
    font-weight: 800;
    color: var(--number-color);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    flex-shrink: 0;
}

.project-title {
    font-size: 0.92rem;
    font-weight: 600;
    color: var(--accent);
    line-height: 1.35;
}

.chevron {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    color: var(--text-secondary);
    transition: transform 0.25s ease;
}

.project-card.active .chevron {
    transform: rotate(180deg);
}

/* =====================
   Dropdown Content
   ===================== */
.dropdown-content {
    overflow: hidden;
    height: 0;
    transition: height 0.3s ease-out;
}

.dropdown-content.visible {
    height: auto;
    padding: 0 1.3rem 1.3rem;
}

/* =====================
   Description List
   ===================== */
.description-list {
    list-style: none;
    padding: 0;
    margin: 0 0 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    border-top: 1px solid var(--card-border);
    padding-top: 1rem;
}

.description-item {
    font-size: 0.86rem;
    line-height: 1.75;
    color: var(--text-secondary);
    padding-left: 1.1rem;
    position: relative;
}

.description-item::before {
    content: '▸';
    position: absolute;
    left: 0;
    top: 0.1em;
    color: var(--accent);
    font-size: 0.7rem;
}

/* =====================
   Gallery
   ===================== */
.gallery {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.65rem;
    margin-bottom: 1rem;
}

.gallery-image {
    width: 100%;
    max-height: 320px;
    border-radius: 7px;
    object-fit: contain;
    background: rgba(0, 0, 0, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid var(--card-border);
    cursor: default;
}

.gallery-image:hover {
    transform: scale(1.03);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

/* Single image spans full width */
.gallery-image:only-child {
    grid-column: 1 / -1;
    max-height: 400px;
}

/* =====================
   Link Row
   ===================== */
.link-row {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.link-button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    font-weight: 500;
    padding: 0.4rem 1rem;
    border-radius: 6px;
    text-decoration: none;
    background: var(--accent-subtle);
    border: 1px solid var(--accent-border);
    color: var(--tag-text);
    transition: background 0.2s ease, border-color 0.2s ease;
    letter-spacing: 0.01em;
}

.link-button svg {
    width: 14px;
    height: 14px;
}

.link-button:hover {
    background: rgba(74, 159, 200, 0.18);
    border-color: var(--accent);
}

/* =====================
   Animations
   ===================== */
@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: opacity 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    opacity: 0;
}

/* =====================
   Responsive
   ===================== */
@media (max-width: 640px) {
    .main-container {
        padding: 5rem 1rem 3rem;
    }

    .card-header {
        gap: 0.6rem;
        padding: 0.75rem 1rem;
    }

    .card-thumbnail {
        width: 32px;
        height: 32px;
    }

    .project-number {
        font-size: 1rem;
    }

    .project-title {
        font-size: 0.84rem;
    }

    .gallery {
        grid-template-columns: 1fr;
    }

    .gallery-image {
        max-height: 260px;
    }
}
</style>